import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useBrandTheme, BRAND_LOGO_SRC } from '@/hooks/use-brand-theme';

/**
 * Boot splash: a small "unboxing" animation. A CompBox package appears, its
 * two lid flaps swing open, and the brand logo lifts out — then the whole
 * overlay fades and `onComplete` fires (which lets App redirect to /login).
 *
 * Drop-in replacement for {@link SplashScreen} — same `{ onComplete }`
 * contract, shown once per session. The previous word-cycling "Connected"
 * splash is preserved in `splash-screen.tsx` for easy reversion.
 */

// ── timings (ms) ──
// The choreography finishes ~1.8s in (logo settled); the rest is a deliberate
// hold so the assembled box + logo is on screen for a couple of seconds before
// it fades and hands off to login.
const DONE_MS = 4200;      // total time the animation is on screen
const DONE_MS_REDUCED = 1800;
const FADE_OUT_MS = 600;

export function UnboxingSplash({ onComplete }: { onComplete: () => void }) {
  const { brand } = useBrandTheme();
  const reduce = useReducedMotion();
  const primary = brand === 'blue' ? '#2563EB' : '#C16A2C';
  const tint = brand === 'blue' ? 'rgba(37,99,235,0.10)' : 'rgba(193,106,44,0.10)';

  const [phase, setPhase] = useState<'playing' | 'done'>('playing');

  useEffect(() => {
    const t = setTimeout(() => { setPhase('done'); }, reduce ? DONE_MS_REDUCED : DONE_MS);
    return () => { clearTimeout(t); };
  }, [reduce]);

  const handleExitComplete = useCallback(() => {
    if (phase === 'done') onComplete();
  }, [phase, onComplete]);

  // Reduced motion: skip the choreography, just show the resolved logo briefly.
  const flapLeft = reduce ? {} : { rotate: [0, -116] };
  const flapRight = reduce ? {} : { rotate: [0, 116] };

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {phase !== 'done' && (
        <motion.div
          key="unboxing"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: FADE_OUT_MS / 1000, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background select-none"
        >
          <div
            className="relative"
            style={{ width: 148, height: 128, perspective: 800 }}
            aria-hidden="true"
          >
            {/* Box body (front wall), scales in first */}
            <motion.div
              initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.8, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'absolute',
                left: 14,
                right: 14,
                top: 46,
                bottom: 6,
                borderRadius: '5px 5px 9px 9px',
                border: `2px solid ${primary}`,
                background: `linear-gradient(180deg, ${tint}, transparent)`,
                boxShadow: `0 18px 40px -18px ${primary}`,
              }}
            >
              {/* center seam */}
              <div
                style={{
                  position: 'absolute', top: 0, bottom: 0, left: '50%',
                  width: 1.5, background: primary, opacity: 0.35, transform: 'translateX(-50%)',
                }}
              />
            </motion.div>

            {/* Left lid flap — hinged at its outer (left) edge */}
            <motion.div
              initial={{ rotate: 0 }}
              animate={flapLeft}
              transition={{ delay: 0.55, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'absolute', top: 40, left: 14, width: 60, height: 13,
                transformOrigin: 'left center',
                borderRadius: 3,
                border: `2px solid ${primary}`,
                background: tint,
              }}
            />
            {/* Right lid flap — hinged at its outer (right) edge */}
            <motion.div
              initial={{ rotate: 0 }}
              animate={flapRight}
              transition={{ delay: 0.55, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'absolute', top: 40, right: 14, width: 60, height: 13,
                transformOrigin: 'right center',
                borderRadius: 3,
                border: `2px solid ${primary}`,
                background: tint,
              }}
            />

            {/* Burst particles rising out of the box */}
            {!reduce && [-26, -12, 4, 18, 30].map((dx, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, x: 0, y: 60, scale: 0 }}
                animate={{ opacity: [0, 1, 0], x: dx, y: [60, 4], scale: [0, 1, 0.6] }}
                transition={{ delay: 1.02 + i * 0.04, duration: 0.7, ease: 'easeOut' }}
                style={{
                  position: 'absolute', top: 34, left: '50%', width: 6, height: 6,
                  marginLeft: -3, borderRadius: '50%', background: primary,
                }}
              />
            ))}

            {/* Logo lifts out of the box */}
            <motion.img
              src={BRAND_LOGO_SRC[brand]}
              alt=""
              initial={reduce ? { opacity: 1, y: -30, scale: 1 } : { opacity: 0, y: 40, scale: 0.35 }}
              animate={{ opacity: 1, y: -30, scale: 1 }}
              transition={reduce ? { duration: 0.2 } : { delay: 0.95, type: 'spring', stiffness: 140, damping: 12 }}
              style={{
                position: 'absolute', top: 30, left: '50%', height: 44,
                marginLeft: -22, // half of rendered width (~44 square)
              }}
            />
          </div>
          {/* No wordmark/copyright — the logo cube emerging from the box is the
              brand moment on its own. (Removed to avoid overlapping the box.) */}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
